import React from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { FaShoppingCart, FaStar } from "react-icons/fa";
import { ProductSkeleton } from "../../components/common/ProductSkeleton";
import { useCart } from "../../hooks/CartContext";

const FeaturedProducts = () => {
  const { addToCart } = useCart();
  const navigate = useNavigate();

  const { data, isPending, isError, error } = useQuery({
    queryKey: ["featured-products"],
    queryFn: async () => {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/products/featuredProduct`
      );
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.message || "Something went wrong");
      return json;
    },
  });

  const products = data?.products ?? [];

  return (
    <div className="min-h-screen bg-gray-100">
      <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 py-6 sm:py-10">
        <h2 className="text-gray-900 font-bold text-2xl sm:text-3xl mb-5 sm:mb-6">
          Featured <span className="text-orange-600">Products</span>
        </h2>

        {/* Error */}
        {isError && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
            <p className="text-sm text-red">{error?.message}</p>
          </div>
        )}

        {/* Loading */}
        {isPending && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-6">
            {Array.from({ length: 12 }).map((_, i) => (
              <ProductSkeleton key={i} />
            ))}
          </div>
        )}

        {/* Empty */}
        {!isPending && !isError && products.length === 0 && (
          <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8 text-center">
            <p className="text-gray-900 font-semibold">No featured products yet</p>
            <p className="text-sm text-gray-500 mt-1">Please check back later.</p>
          </div>
        )}

        {/* Grid */}
        {!isPending && !isError && products.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 sm:gap-5">
            {products.map((p) => {
              const soldOut = Number(p?.quantity || 0) < 1;

              return (
                <div
                  key={p._id}
                  onClick={() => navigate(`/product/${p._id}`)}
                  className="bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-md cursor-pointer transition overflow-hidden"
                >
                  <img
                    src={p?.images?.[0] || "/placeholder.png"}
                    alt={p?.name || "Product"}
                    className="h-40 sm:h-48 w-full object-cover bg-gray-100"
                  />

                  <div className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate">
                          {p?.name}
                        </h3>
                        <p className="text-xs text-gray-500 truncate">
                          {p?.category}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between mt-2 text-sm">
                      <span className="flex items-center gap-1 text-yellow-500 font-bold">
                        <FaStar /> {p?.averageRating || 0}
                      </span>
                      {/* <span className={`font-medium text-xs ${soldOut ? "text-gray-500" : "text-red"}`}>
                        {soldOut ? "Sold out" : `${p?.quantity} unit left`}
                      </span> */}
                    </div>

                    <div className="mt-3 flex items-center justify-between">
                      <p className="text-gray-900 font-bold">
                        ₦{Number(p?.finalPrice || 0).toLocaleString()}
                      </p>
                      {!!p?.price && (
                        <p className="text-red font-bold line-through text-sm">
                          ₦{Number(p?.price || 0).toLocaleString()}
                        </p>
                      )}
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        if (soldOut) return;
                        addToCart(p);
                      }}
                      disabled={soldOut}
                      className={`mt-4 w-full py-2.5 rounded-xl flex items-center justify-center gap-2 font-semibold transition
                        ${
                          soldOut
                            ? "bg-gray-200 text-gray-600 cursor-not-allowed"
                            : "bg-red text-white hover:opacity-90"
                        }`}
                    >
                      <FaShoppingCart />
                      {soldOut ? "Sold Out" : "Add to Cart"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default FeaturedProducts;
